/*
Функция генерирует массив с начальным и конечным значениями,
на каждом элементе массива указывается значение в часах и минутах.
Если нужно изменить последующий шаг, передается массив с этапом,
на котором будет меняться шаг и размер шага.

Также мы можем указать значения на которых шаг будет меняться
fn(50, 300, 10, [{stage: 120, step: 15}, {stage: 180, step: 30}]) =>
[
  {value: 50, h: 0, m: 50},
  ...шаr 10
  {value: 110, h: 1, m: 50},
  {value: 120, h: 2, m: 0},
  ...шаr 15
  {value: 165, h: 2, m: 45},
  {value: 180, h: 3, m: 0},
  ...шаr 30
  {value: 300, h: 5, m: 0},
]
* */

import { declensionWord } from '../../../helpers/helpers';

type HoursMinutes = {
  hours: number;
  minutes: number;
};

type MinutesStepStage = {
  stage: number;
  step: number;
};

type MinutesRangeOption = {
  value: number;
  h: number;
  m: number;
  hWithDeclension: string;
  mWithDeclension: string;
};

const toHoursMinutes = (totalMinutes: number): HoursMinutes => {
  const safeMinutes = Number.isFinite(totalMinutes) ? totalMinutes : 0;
  const abs = Math.abs(Math.trunc(safeMinutes));
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;

  return {
    hours: safeMinutes < 0 ? -hours : hours,
    minutes,
  };
};

export const buildMinutesRangeOptions = (
  startMinutes: number,
  endMinutes: number,
  stepMinutes: number,
  stages: MinutesStepStage[] = [],
): MinutesRangeOption[] => {
  const start = Math.trunc(startMinutes);
  const end = Math.trunc(endMinutes);
  const baseStep = Math.trunc(stepMinutes);

  if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    !Number.isFinite(baseStep) ||
    baseStep <= 0
  ) {
    return [];
  }
  if (end < start) {
    return [];
  }

  const normalizedStages = stages
    .filter((s) => Number.isFinite(s?.stage) && Number.isFinite(s?.step))
    .map((s) => ({ stage: Math.trunc(s.stage), step: Math.trunc(s.step) }))
    .filter((s) => s.step > 0)
    .filter((s) => s.stage >= start && s.stage <= end)
    .sort((a, b) => a.stage - b.stage);

  const result: MinutesRangeOption[] = [];
  const seen = new Set<number>();

  const pushValue = (value: number) => {
    if (value < start || value > end) return;
    if (seen.has(value)) return;
    seen.add(value);

    const hm = toHoursMinutes(value);
    result.push({
      value,
      h: hm.hours,
      m: hm.minutes,
      hWithDeclension: hm.hours
        ? `${hm.hours} ${declensionWord(hm.hours, ['час', 'часа', 'часов'])} `
        : '',
      mWithDeclension: hm.minutes
        ? `${hm.minutes} ${declensionWord(hm.minutes, ['минута', 'минуты', 'минут'])}`
        : '',
    });
  };

  let current = start;
  let currentStep = baseStep;
  let stageIndex = 0;

  while (current <= end) {
    pushValue(current);

    // если текущая точка — это stage, обновляем шаг
    while (stageIndex < normalizedStages.length && normalizedStages[stageIndex].stage === current) {
      currentStep = normalizedStages[stageIndex].step;
      stageIndex += 1;
    }

    if (current === end) break;

    const nextByStep = current + currentStep;
    const nextStage =
      stageIndex < normalizedStages.length ? normalizedStages[stageIndex].stage : undefined;

    // гарантируем, что stage не будет “перепрыгнут”
    const next =
      typeof nextStage === 'number' && nextStage > current && nextStage < nextByStep
        ? nextStage
        : nextByStep;

    if (next <= current) break;

    current = next > end ? end : next;
  }

  return result.sort((a, b) => a.value - b.value);
};
